/**
 * PauseScreen - Pause overlay during gameplay
 * Semi-transparent overlay with PAUSED text and options: Resume, Settings, Quit to Title.
 * Renders the frozen gameplay behind the overlay.
 */

export class PauseScreen {
  constructor(game) {
    this.game = game;
    this.menuOptions = ['Resume', 'Settings', 'Quit to Title'];
    this.selectedIndex = 0;
  }

  enter(data = {}) {
    this.selectedIndex = 0;
    this._updateOverlay();
  }

  exit() {
    // Clear overlay when leaving pause screen
    if (this.game.uiOverlay) {
      this.game.uiOverlay.innerHTML = '';
    }
  }

  update(dt) {
    const input = this.game.input;

    if (input.isKeyJustPressed('Escape')) {
      // Resume without resetting game state
      this._resume();
      return;
    }

    if (input.isKeyJustPressed('ArrowDown')) {
      this.selectedIndex = (this.selectedIndex + 1) % this.menuOptions.length;
      this._updateOverlay();
    }
    if (input.isKeyJustPressed('ArrowUp')) {
      this.selectedIndex = (this.selectedIndex - 1 + this.menuOptions.length) % this.menuOptions.length;
      this._updateOverlay();
    }
    if (input.isKeyJustPressed('Enter') || input.isKeyJustPressed('Space')) {
      this._selectOption(this.selectedIndex);
    }
    if (input.isKeyJustPressed('Tab')) {
      this.selectedIndex = (this.selectedIndex + 1) % this.menuOptions.length;
      this._updateOverlay();
    }

    if (input.wasClicked()) {
      const pos = input.getPointerPosition();
      const clickedIndex = this._getOptionAtPosition(pos.x, pos.y);
      if (clickedIndex !== -1) {
        this._selectOption(clickedIndex);
      }
    }
  }

  _resume() {
    // Resume gameplay without calling enter() which would reset the game
    // We bypass setState to avoid triggering PlayingState.enter()
    this.exit(); // Clean up overlay
    this.game.state.previousState = 'paused';
    this.game.state.currentState = 'playing';
    // Trigger the playing state to refresh its overlay
    const playingState = this.game.state.states['playing'];
    if (playingState && playingState._updateOverlay) {
      playingState._updateOverlay();
    }
  }

  _selectOption(index) {
    switch (index) {
      case 0: // Resume
        this._resume();
        break;
      case 1: // Settings
        this.game.state.setState('settings');
        break;
      case 2: // Quit to Title
        this.game.state.setState('title');
        break;
    }
  }

  _getOptionAtPosition(x, y) {
    const w = this.game.width;
    const h = this.game.height;
    const startY = h * 0.5;
    const spacing = h * 0.08;
    const hitWidth = Math.max(200, w * 0.4); // Min 200px for touch
    const hitHeight = Math.max(44, spacing * 0.8); // Min 44px for touch targets

    for (let i = 0; i < this.menuOptions.length; i++) {
      const optionY = startY + i * spacing;
      const left = w / 2 - hitWidth / 2;
      const top = optionY - hitHeight / 2;

      if (x >= left && x <= left + hitWidth && y >= top && y <= top + hitHeight) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Update the DOM overlay with pause menu content for Playwright testing
   */
  _updateOverlay() {
    if (!this.game.uiOverlay) return;

    const menuHtml = this.menuOptions.map((opt, i) => {
      const selected = i === this.selectedIndex ? ' data-selected="true"' : '';
      return `<div class="menu-option" data-index="${i}"${selected}>${opt}</div>`;
    }).join('');

    this.game.uiOverlay.innerHTML = `
      <div data-screen="paused">
        <div class="pause-title">PAUSED</div>
        <div class="menu-options">${menuHtml}</div>
      </div>
    `;
  }

  render(ctx) {
    const w = this.game.width;
    const h = this.game.height;

    // First render the frozen gameplay behind the overlay
    const playingState = this.game.state.states['playing'];
    if (playingState && playingState.render) {
      playingState.render(ctx);
    }

    // Semi-transparent overlay to dim the gameplay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(0, 0, w, h);

    // PAUSED text
    this.game.renderer.drawGlowText('PAUSED', w / 2, h * 0.3, {
      font: `${Math.max(36, w * 0.05)}px "Share Tech Mono", monospace`,
      color: '#ffffff',
      glowColor: '#00ffff',
      glowSize: 20,
    });

    // Menu options
    const startY = h * 0.5;
    const spacing = h * 0.08;
    this.menuOptions.forEach((option, i) => {
      const isSelected = i === this.selectedIndex;

      // Selection indicator
      if (isSelected) {
        const indicatorX = w / 2 - w * 0.12;
        this.game.renderer.drawGlowText('\u25B6', indicatorX, startY + i * spacing, {
          font: `${Math.max(14, w * 0.018)}px "Share Tech Mono", monospace`,
          color: '#00ffff',
          glowColor: '#00ffff',
          glowSize: 10,
        });
      }

      this.game.renderer.drawGlowText(option, w / 2, startY + i * spacing, {
        font: `${Math.max(18, w * 0.025)}px "Share Tech Mono", monospace`,
        color: isSelected ? '#00ffff' : '#aabbcc',
        glowColor: isSelected ? '#00ffff' : '#445566',
        glowSize: isSelected ? 15 : 3,
      });
    });

    // Footer instruction
    this.game.renderer.drawGlowText('PRESS ESC TO RESUME', w / 2, h * 0.85, {
      font: `${Math.max(10, w * 0.012)}px "Share Tech Mono", monospace`,
      color: '#8899aa',
      glowColor: '#445566',
      glowSize: 2,
    });
  }
}
